import { Controller } from "@hotwired/stimulus"

// Owns the docked assistant panel: whether it is on screen, how wide it is, and
// how it gets out of the way of top-nav overlays (search `#modal`, Recents and
// other header dropdowns, and the shared `#sidepanel`). The dock element is
// `data-turbo-permanent`, so unlike an ordinary sidepanel nothing tears the
// panel down on navigation — this controller is the only thing that decides
// whether it is visible.
//
// Three states, mirrored into a cookie so the server renders the right one on
// the first paint instead of flashing:
//   open      — panel on screen
//   minimized — hidden, but the chat is only parked: it keeps streaming and
//               reopening resumes exactly where the user left off
//   closed    — hidden and emptied; the conversation is done, so reopening
//               starts a blank chat
export default class extends Controller {
  static targets = ["panel", "frame", "expandButton", "handle"]
  static values = {
    state: { type: String, default: "closed" },
    url: String,
  }

  static STATE_COOKIE = "hudini_panel"
  static EXPANDED_STORAGE_KEY = "hudini-panel-expanded"
  static HANDLE_STORAGE_KEY = "hudini-handle-position"
  static HEADER_TRIGGER = "#ai-shortcut"
  // Openers that live outside the dock ask for the panel with this event rather
  // than reaching for the controller instance.
  static OPEN_EVENT = "hudini:open"
  // Matches the slide duration in _hudini_dock.scss. `transitionend` is not
  // reliable here (reduced-motion users get no transition at all), so the
  // teardown after closing is scheduled instead of listened for.
  static SLIDE_MS = 300
  // Pointer travel that separates a click on the handle from a drag of it.
  // Trackpads drift a few pixels during an ordinary click, so this has to be
  // wider than a mouse alone would need.
  static DRAG_THRESHOLD_PX = 8
  // How long a click on the handle is ignored after dragging it. Long enough
  // for the browser to deliver the click that follows the drag's pointerup,
  // short enough that it cannot swallow the user's next real click.
  static DRAG_CLICK_GRACE_MS = 250
  // Keeps a dropped handle clear of the header and the bottom of the viewport.
  static HANDLE_MIN_TOP = 12
  static HANDLE_MAX_TOP = 88

  get expandedIcon() {
    return "<i class='far fa-arrow-down-left-and-arrow-up-right-to-center'></i>"
  }

  get collapsedIcon() {
    return "<i class='far fa-arrow-up-right-and-arrow-down-left-from-center'></i>"
  }

  get frameLoaded() {
    if (!this.hasFrameTarget || this.frameTarget.childElementCount === 0) return false
    // The open-state skeleton is not a loaded panel.
    return !this.frameTarget.querySelector(".hudini-dock__loading")
  }

  connect() {
    this.onTriggerClick = this.onTriggerClick.bind(this)
    this.onFrameFetch = this.onFrameFetch.bind(this)
    this.onOpenRequest = this.onOpenRequest.bind(this)
    this.onKeydown = this.onKeydown.bind(this)
    this.onTurboLoad = this.onTurboLoad.bind(this)
    this.onDragMove = this.onDragMove.bind(this)
    this.onDragEnd = this.onDragEnd.bind(this)

    // Capture phase: the header trigger is a Turbo Frame link, so the toggle
    // has to win before Turbo starts a fetch we do not want.
    document.addEventListener("click", this.onTriggerClick, true)
    document.addEventListener(this.constructor.OPEN_EVENT, this.onOpenRequest)
    // The header and sidepanel are re-rendered by every visit, so the indicator
    // and the observer have to be re-applied to the new nodes.
    document.addEventListener("turbo:load", this.onTurboLoad)
    this.element.addEventListener("turbo:before-fetch-request", this.onFrameFetch)
    // Document bubble so Escape still minimizes when focus has left the panel
    // (e.g. a click on the page behind). Children that own Escape (stop stream,
    // skill menu, clarifying questions) stopPropagation before we see it.
    document.addEventListener("keydown", this.onKeydown)

    this.applyExpanded(this.readPersistedExpanded())
    this.applyPosition(this.readPersistedPosition())
    this.applyState(this.stateValue)
    this.watchOverlays()
    // Full refresh with an open cookie used to set turbo-frame[src] in the HTML,
    // racing the host page. Wait for first paint, then resume the chat.
    if (this.stateValue === "open") this.scheduleOpenPanelLoad()
  }

  // Two rAFs: after layout + paint of the host page, then fetch the panel.
  scheduleOpenPanelLoad() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.loadOpenPanel())
    })
  }

  loadOpenPanel() {
    if (this.stateValue !== "open" || this.frameLoaded || !this.hasFrameTarget) return

    const trigger = document.querySelector(this.constructor.HEADER_TRIGGER)
    this.loadFrame(this.frameUrl(trigger ? trigger.href : this.urlValue, null))
  }

  disconnect() {
    document.removeEventListener("click", this.onTriggerClick, true)
    document.removeEventListener(this.constructor.OPEN_EVENT, this.onOpenRequest)
    document.removeEventListener("turbo:load", this.onTurboLoad)
    document.removeEventListener("keydown", this.onKeydown)
    this.element.removeEventListener("turbo:before-fetch-request", this.onFrameFetch)
    this.unbindDragListeners()

    // A navigation can land between closing and the slide finishing; finish the
    // teardown now so reopening still starts on a blank chat.
    this.flushClose()
    this.disconnectOverlayObservers()
  }

  // The header controls ship with the panel body, so the expand icon can only
  // be pointed the right way once the frame has loaded.
  expandButtonTargetConnected(button) {
    button.innerHTML = this.isExpanded ? this.expandedIcon : this.collapsedIcon
  }

  // --- Actions ---------------------------------------------------------------

  open() {
    this.setState("open")
    this.loadIfEmpty()
    this.focusComposer()
  }

  // Shared by the header button and the side handle: minimizing parked the
  // chat, so both bring back the same conversation rather than starting over.
  restore() {
    if (this.frameLoaded) return this.open()

    // A full page load empties the frame; refetch what the server would give
    // back, keeping whatever record context the header link carries.
    const trigger = document.querySelector(this.constructor.HEADER_TRIGGER)
    this.reopen(trigger ? trigger.href : this.urlValue, null)
  }

  minimize() {
    this.setState("minimized")
  }

  close() {
    this.setState("closed")

    // Let the panel slide out before emptying it, and leave any in-flight run
    // alone: it finishes server-side and the reply is still in the chat's
    // history even though the next open starts somewhere new.
    if (this.closeTimeout) clearTimeout(this.closeTimeout)
    this.closeTimeout = setTimeout(() => this.flushClose(), this.constructor.SLIDE_MS)
  }

  flushClose() {
    if (!this.closeTimeout) return
    clearTimeout(this.closeTimeout)
    this.closeTimeout = null
    // The user may have reopened during the slide, in which case the loaded
    // chat is back on screen and must be left alone.
    if (this.stateValue === "closed") this.teardownFrame()
  }

  teardownFrame() {
    if (!this.hasFrameTarget) return

    this.frameTarget.removeAttribute("src")
    this.frameTarget.innerHTML = ""
  }

  toggleExpanded(event) {
    if (event) event.preventDefault()
    this.applyExpanded(!this.isExpanded)
    this.persistExpanded(this.isExpanded)
  }

  // --- Events ----------------------------------------------------------------

  // The header entry point is a toggle, and what it reopens depends on how the
  // panel was dismissed: minimizing parks a conversation, closing ends one.
  onTriggerClick(event) {
    const trigger = event.target.closest?.(this.constructor.HEADER_TRIGGER)
    if (!trigger) return

    event.preventDefault()
    event.stopPropagation()

    // A panel yielding to an overlay is off screen, so this click is the user
    // asking for it back. Minimizing would look like the click did nothing and
    // take a second one to undo.
    if (this.stateValue === "open" && !this.yielded) return this.minimize()
    if (this.stateValue !== "closed") return this.restore()
    // Closing ended the last conversation, so this one starts blank.
    this.reopen(trigger.href, "new")
  }

  // Openers outside the dock — AI Search in the header search panel and on the
  // advanced search page — submit their own request into `#hudini_panel`, which
  // replaces whatever chat was loaded, so they only need the dock brought on
  // screen. `href` is honoured for callers that would rather the dock do the
  // loading itself.
  onOpenRequest(event) {
    const { href, mode } = event.detail || {}
    if (href) return this.reopen(href, mode)

    this.setState("open")
  }

  reopen(href, mode) {
    this.teardownFrame()
    // The panel shell has no background of its own — chrome lives inside the
    // frame. Sliding an empty frame in looks like a pop when the response
    // lands; the loading card gives the same solid surface as restore-from-
    // minimized, so the transform transition reads the same.
    this.showOpenSkeleton()
    this.setState("open")
    if (this.hasFrameTarget) this.loadFrame(this.frameUrl(href, mode))
  }

  showOpenSkeleton() {
    if (!this.hasFrameTarget) return

    this.frameTarget.innerHTML = `
      <div class="hudini-dock__loading" aria-busy="true" aria-label="Loading assistant">
        <span class="hudini-typing" aria-hidden="true"><span></span><span></span><span></span></span>
      </div>
    `
  }

  // Turbo watches the `src` attribute, so set it rather than the property.
  loadFrame(url) {
    this.frameTarget.setAttribute("src", url)
  }

  // The trigger's href carries the current page as chat context; `mode` decides
  // whether the server resumes the last chat or hands back a blank one.
  frameUrl(href, mode) {
    const url = new URL(href, window.location.origin)
    if (mode) url.searchParams.set("mode", mode)
    return `${url.pathname}${url.search}`
  }

  // Anything loading into the frame — the header, a record's "Add to current AI
  // chat", the chat switcher — means the user wants the assistant on screen.
  onFrameFetch(event) {
    if (this.stateValue !== "open") this.setState("open")
    // The in-panel "+" posts to create without the page record; borrow it from
    // the header link so a new chat on an article/company page attaches the
    // same way opening the dock does.
    this.attachPageRecord(event)
  }

  // Copies record_type/record_id off `#ai-shortcut` onto a new-chat create.
  // That link is re-rendered on every Turbo visit, so it always reflects the
  // page under the permanent dock.
  attachPageRecord(event) {
    const { url, fetchOptions } = event?.detail || {}
    if (!url || !fetchOptions) return

    const method = (fetchOptions.method || "GET").toUpperCase()
    const path = new URL(url, window.location.origin).pathname
    if (method !== "POST" || path !== this.urlValue) return

    const page = this.headerRecordParams()
    if (!page) return

    const body = fetchOptions.body
    if (!(body instanceof FormData)) return
    if (body.has("record_type") || body.has("record_id")) return

    body.append("record_type", page.recordType)
    body.append("record_id", page.recordId)
  }

  headerRecordParams() {
    const trigger = document.querySelector(this.constructor.HEADER_TRIGGER)
    if (!trigger?.href) return null

    const page = new URL(trigger.href, window.location.origin)
    const recordType = page.searchParams.get("record_type")
    const recordId = page.searchParams.get("record_id")
    if (!recordType || !recordId) return null

    return { recordType, recordId }
  }

  onKeydown(event) {
    if (this.stateValue !== "open" || this.yielded) return

    if (event.key === "Escape") {
      // The composer and the clarifying-questions card claim Escape first while
      // they are busy; reaching here means nothing else wanted it.
      event.preventDefault()
      this.minimize()
      return
    }

    if (event.key === "Tab") this.trapFocus(event)
  }

  // Keep Tab cycling inside the open panel so focus cannot escape onto the
  // page behind it (Send → Minimize → … → Send).
  trapFocus(event) {
    if (!this.hasPanelTarget || !this.panelTarget.contains(document.activeElement)) return

    const focusable = this.focusableElements()
    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    }
  }

  focusableElements() {
    if (!this.hasPanelTarget) return []

    // Contenteditable composer is the main prompt; include it alongside the
    // usual controls. getClientRects() skips display:none / zero-size nodes
    // (hidden menus, the file input) without offsetParent's fixed-position
    // false negatives. tabIndex !== -1 drops natively-focusable tags that
    // have been taken out of the tab order.
    const selector = 'a[href], button, input, textarea, select, details, [contenteditable="true"], [tabindex]:not([tabindex="-1"])'
    return Array.from(this.panelTarget.querySelectorAll(selector)).filter(
      (el) => !el.hasAttribute("disabled") && el.tabIndex !== -1 && el.getClientRects().length > 0,
    )
  }

  onTurboLoad() {
    this.watchOverlays()
  }

  // --- State -----------------------------------------------------------------

  setState(state) {
    this.applyState(state)
    this.persistState(state)
  }

  applyState(state) {
    this.stateValue = state

    this.element.classList.toggle("hudini-dock--open", state === "open")
    this.element.classList.toggle("hudini-dock--minimized", state === "minimized")
    this.element.classList.toggle("hudini-dock--closed", state === "closed")
    // A hidden panel keeps rendering streamed updates, so it has to be taken
    // out of the tab order and the accessibility tree explicitly. Only the
    // panel — the handle is the way back in.
    if (this.hasPanelTarget) this.panelTarget.inert = state !== "open"
  }

  focusComposer() {
    // An off-screen panel must not pull focus out of the overlay covering it.
    if (this.yielded) return

    const prompt = this.element.querySelector("[data-hudini-target='prompt']")
    if (prompt) prompt.focus()
  }

  applyExpanded(expanded) {
    this.isExpanded = expanded
    this.element.classList.toggle("hudini-dock--expanded", expanded)

    if (this.hasExpandButtonTarget) {
      this.expandButtonTarget.innerHTML = expanded ? this.expandedIcon : this.collapsedIcon
    }
  }

  loadIfEmpty() {
    if (this.frameLoaded || !this.hasFrameTarget || !this.hasUrlValue) return

    if (this.frameTarget.getAttribute("src") === this.urlValue) {
      this.frameTarget.reload()
    } else {
      this.loadFrame(this.urlValue)
    }
  }

  // --- Overlay handoff --------------------------------------------------------

  // Top-nav panels (search modal, Recents/Favorites dropdowns, shared sidepanel)
  // sit under or behind the floating dock. Step aside while any of them is open
  // and slide back when the last one closes.
  watchOverlays() {
    this.disconnectOverlayObservers()
    this.overlayObservers = []

    this.observeOverlayFrame("sidepanel")
    this.observeOverlayFrame("modal")
    this.observeHeaderDropdowns()
    this.refreshYield()
  }

  disconnectOverlayObservers() {
    this.overlayObservers?.forEach((observer) => observer.disconnect())
    this.overlayObservers = []
  }

  observeOverlayFrame(id) {
    const frame = document.getElementById(id)
    if (!frame) return

    const observer = new MutationObserver(() => this.refreshYield())
    observer.observe(frame, { childList: true })
    this.overlayObservers.push(observer)
  }

  observeHeaderDropdowns() {
    const header = document.querySelector(".header")
    if (!header) return

    const observer = new MutationObserver(() => this.refreshYield())
    observer.observe(header, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["class"],
    })
    this.overlayObservers.push(observer)
  }

  refreshYield() {
    this.applyYield(this.overlayOpen())
  }

  overlayOpen() {
    return this.frameOccupied("sidepanel")
      || this.frameOccupied("modal")
      || this.headerDropdownOpen()
  }

  frameOccupied(id) {
    const frame = document.getElementById(id)
    return !!(frame && frame.childElementCount > 0)
  }

  headerDropdownOpen() {
    return !!document.querySelector(".header .dropdown__content:not(.hidden)")
  }

  applyYield(yielded) {
    this.element.classList.toggle("hudini-dock--yielded", yielded)
  }

  get yielded() {
    return this.element.classList.contains("hudini-dock--yielded")
  }

  // --- Drag (handle + panel) ----------------------------------------------------

  restoreFromHandle(event) {
    event.preventDefault()
    // The pointerup that ended a drag is followed by a click; that click was
    // the user letting go of the handle, not asking to open it. Scoped to the
    // moment the drag ended rather than latched until the next click, which
    // went stale whenever no click followed and then ate a real one later.
    if (this.handleDragJustEnded()) return

    this.restore()
  }

  handleDragJustEnded() {
    if (!this.handleDragEndedAt) return false
    return Date.now() - this.handleDragEndedAt < this.constructor.DRAG_CLICK_GRACE_MS
  }

  // Minimized tab: centre under the cursor while dragging, snap to an edge.
  startDrag(event) {
    if (event.button !== 0 || !this.hasHandleTarget) return
    this.beginDrag(event, "handle")
  }

  // Open panel: grab from the title bar. Buttons in the header stay clickable.
  startPanelDrag(event) {
    if (event.button !== 0 || !this.hasPanelTarget) return
    if (event.target.closest("button, a, input, textarea, select")) return

    const rect = this.panelTarget.getBoundingClientRect()
    this.beginDrag(event, "panel", {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      width: rect.width,
      height: rect.height,
    })
  }

  beginDrag(event, kind, extras = {}) {
    this.dragKind = kind
    this.dragOrigin = { x: event.clientX, y: event.clientY }
    this.dragPointer = { x: event.clientX, y: event.clientY }
    this.dragExtras = extras
    this.dragging = false
    this.dragPointerId = event.pointerId

    // Document listeners keep the drag tracking the pointer even when it
    // leaves the grip — element-bound `pointermove` alone feels jumpy.
    document.addEventListener("pointermove", this.onDragMove)
    document.addEventListener("pointerup", this.onDragEnd)
    document.addEventListener("pointercancel", this.onDragEnd)
  }

  onDragMove(event) {
    if (!this.dragOrigin || event.pointerId !== this.dragPointerId) return

    this.dragPointer = { x: event.clientX, y: event.clientY }

    const travelled = Math.hypot(event.clientX - this.dragOrigin.x, event.clientY - this.dragOrigin.y)
    if (!this.dragging && travelled < this.constructor.DRAG_THRESHOLD_PX) return

    if (!this.dragging) this.enterDrag()

    if (this.dragFrame) return
    this.dragFrame = requestAnimationFrame(() => {
      this.dragFrame = null
      if (!this.dragging) return
      this.paintDrag()
    })
  }

  enterDrag() {
    this.dragging = true
    document.body.style.cursor = "grabbing"
    document.body.style.userSelect = "none"

    if (this.dragKind === "handle" && this.hasHandleTarget) {
      this.handleTarget.classList.add("hudini-dock__handle--dragging")
      this.handleTarget.style.right = "auto"

      // The handle tracks the pointer, so its "Resume AI chat" tooltip would
      // ride along for the whole drag. Hide covers the tip already shown by
      // pointerdown's focus; disable keeps hover from re-showing it mid-drag.
      const tooltip = this.handleTarget._tippy
      if (tooltip) {
        tooltip.hide()
        tooltip.disable()
      }
      return
    }

    if (this.dragKind === "panel" && this.hasPanelTarget) {
      this.panelTarget.classList.add("hudini-dock__panel--dragging")
      this.panelTarget.style.width = `${this.dragExtras.width}px`
      this.panelTarget.style.height = `${this.dragExtras.height}px`
    }
  }

  paintDrag() {
    const { x, y } = this.dragPointer

    if (this.dragKind === "handle" && this.hasHandleTarget) {
      this.handleTarget.style.left = `${x}px`
      this.handleTarget.style.top = `${y}px`
      return
    }

    if (this.dragKind === "panel" && this.hasPanelTarget) {
      // Keep the grab point under the cursor rather than jumping to centre.
      this.panelTarget.style.left = `${x - this.dragExtras.offsetX}px`
      this.panelTarget.style.top = `${y - this.dragExtras.offsetY}px`
    }
  }

  // Both the handle and the panel rest on a corner, so wherever they are let
  // go they snap to the nearest of the four. The handle also keeps its height
  // along that edge so a minimized tab can sit higher or lower.
  onDragEnd(event) {
    if (!this.dragOrigin || (event && event.pointerId !== this.dragPointerId)) return

    const { x, y } = this.dragPointer || this.dragOrigin
    const kind = this.dragKind
    const wasDragging = this.dragging

    // The header grab lives near the top of a ~75vh panel, so the pointer
    // alone almost always sits in the upper half and used to refuse to snap
    // bottom. Reconstruct the panel centre from the grab offset (not the
    // painted rect) so a missed animation frame can't leave us deciding from
    // a stale position.
    let snapX = x
    let snapY = y
    if (wasDragging && kind === "panel" && this.dragExtras) {
      const { offsetX, offsetY, width, height } = this.dragExtras
      snapX = x - offsetX + width / 2
      snapY = y - offsetY + height / 2
    }

    this.unbindDragListeners()
    this.dragOrigin = null
    this.dragPointer = null
    this.dragPointerId = null
    this.dragKind = null
    this.dragExtras = null
    this.dragging = false

    if (!wasDragging) return

    const corner = this.snapCorner(snapX, snapY)

    if (kind === "handle") {
      // Only a handle drag is followed by a click on the handle. Dragging the
      // open panel used to arm this too, leaving the next click on the handle
      // to be thrown away.
      this.handleDragEndedAt = Date.now()
      this.finishHandleDrag(corner, y)
    } else if (kind === "panel") {
      this.finishPanelDrag(corner, snapY)
    }
  }

  // Nearest corner from a drop point: left/right by the vertical midline,
  // top/bottom by the horizontal midline.
  snapCorner(x, y) {
    return {
      side: x < window.innerWidth / 2 ? "left" : "right",
      vertical: y < window.innerHeight / 2 ? "top" : "bottom",
    }
  }

  finishHandleDrag({ side, vertical }, y) {
    if (!this.hasHandleTarget) return

    this.handleTarget.classList.remove("hudini-dock__handle--dragging")
    this.handleTarget.style.left = ""
    this.handleTarget.style.right = ""

    // Parked again, so the tooltip goes back to work. It stays hidden until
    // the pointer leaves and re-enters — re-enabling does not re-show it.
    this.handleTarget._tippy?.enable()

    const position = {
      side,
      vertical,
      top: this.clampHandleTop((y / window.innerHeight) * 100),
    }
    this.applyPosition(position)
    this.persistPosition(position)
  }

  finishPanelDrag({ side, vertical }, y) {
    if (!this.hasPanelTarget) return

    this.panelTarget.classList.remove("hudini-dock__panel--dragging")
    this.panelTarget.style.left = ""
    this.panelTarget.style.top = ""
    this.panelTarget.style.width = ""
    this.panelTarget.style.height = ""

    // Keep the minimized handle near the same corner the panel just parked in.
    const position = {
      side,
      vertical,
      top: this.clampHandleTop((y / window.innerHeight) * 100),
    }
    this.applyPosition(position)
    this.persistPosition(position)
  }

  // Kept for the Stimulus `pointermove`/`pointerup` actions so tests can drive
  // the same path without relying on document capture.
  drag(event) {
    this.onDragMove(event)
  }

  endDrag(event) {
    this.onDragEnd(event)
  }

  unbindDragListeners() {
    if (this.dragFrame) {
      cancelAnimationFrame(this.dragFrame)
      this.dragFrame = null
    }
    document.removeEventListener("pointermove", this.onDragMove)
    document.removeEventListener("pointerup", this.onDragEnd)
    document.removeEventListener("pointercancel", this.onDragEnd)
    document.body.style.cursor = ""
    document.body.style.userSelect = ""
  }

  // Corner is shared by the open panel and the minimized handle so dragging
  // one parks the other on the same corner next time.
  applyPosition({ side, vertical, top }) {
    this.element.classList.toggle("hudini-dock--left", side === "left")
    this.element.classList.toggle("hudini-dock--top", vertical === "top")

    if (this.hasHandleTarget) {
      this.handleTarget.classList.toggle("hudini-dock__handle--left", side === "left")
      this.handleTarget.style.top = `${top}%`
    }
  }

  clampHandleTop(top) {
    return Math.min(this.constructor.HANDLE_MAX_TOP, Math.max(this.constructor.HANDLE_MIN_TOP, top))
  }

  // --- Persistence -------------------------------------------------------------

  // Session cookie (no max-age): closing the browser — or signing out, which
  // deletes this cookie server-side — clears open/minimized, so the next visit
  // starts from closed instead of restoring a parked conversation.
  persistState(state) {
    const secureAttr = window.location.protocol === "https:" ? "; Secure" : ""
    document.cookie = `${this.constructor.STATE_COOKIE}=${state}; path=/; SameSite=Lax${secureAttr}`
  }

  readPersistedExpanded() {
    try {
      return window.localStorage.getItem(this.constructor.EXPANDED_STORAGE_KEY) === "true"
    } catch (e) {
      return false
    }
  }

  persistExpanded(expanded) {
    try {
      window.localStorage.setItem(this.constructor.EXPANDED_STORAGE_KEY, expanded ? "true" : "false")
    } catch (e) {
      // Ignore storage failures (e.g. private mode, disabled storage)
    }
  }

  readPersistedPosition() {
    const fallback = { side: "right", vertical: "bottom", top: 50 }

    try {
      const stored = JSON.parse(window.localStorage.getItem(this.constructor.HANDLE_STORAGE_KEY))
      if (!stored) return fallback

      return {
        side: stored.side === "left" ? "left" : "right",
        // Older saves only stored side/top — infer top vs bottom from the
        // handle height so a remount still opens on a matching corner.
        vertical: stored.vertical === "top" || stored.vertical === "bottom"
          ? stored.vertical
          : (Number.isFinite(stored.top) && stored.top < 50 ? "top" : "bottom"),
        top: Number.isFinite(stored.top) ? this.clampHandleTop(stored.top) : fallback.top,
      }
    } catch (e) {
      return fallback
    }
  }

  persistPosition(position) {
    try {
      window.localStorage.setItem(this.constructor.HANDLE_STORAGE_KEY, JSON.stringify(position))
    } catch (e) {
      // Ignore storage failures (e.g. private mode, disabled storage)
    }
  }
}
